import { get, put } from "@/services/axiosUitils";

export interface GoalsApiResponse {
  success: {
    code: string;
    message: string;
  };
  goals: {
    weight_loss_goal: string;
    weight_loss_reasons: string;
    target_weight_kg: number | null;
    target_weight_lbs: number | null;
    target_weight_in: string;
    activity_level: string;
    diet_type: string;
    intermittent_fasting: string;
    fasting_window_type: string;
  };
}

export interface GoalsData {
  weight_loss_goal: string;
  weight_loss_reasons: string;
  target_weight_kg: number;
  target_weight_lbs: number;
  target_weight_in: string;
  activity_level: string;
  diet_type: string;
  intermittent_fasting: string;
  fasting_window_type: string;
}

class GoalsService {
  private static instance: GoalsService;

  private constructor() {}

  public static getInstance(): GoalsService {
    if (!GoalsService.instance) {
      GoalsService.instance = new GoalsService();
    }
    return GoalsService.instance;
  }

  // Get user goals from API
  async getUserGoals(): Promise<GoalsApiResponse> {
    try {
      const response = await get<GoalsApiResponse>("/food_scanner/goal/");

      return response ?? this.getDefaultGoals();
    } catch (_error) {
      return this.getDefaultGoals();
    }
  }

  private getDefaultGoals(): GoalsApiResponse {
    // Return default goals if API fails
    return {
      success: { code: "fallback", message: "Using default goals" },
      goals: {
        weight_loss_goal: "not_sure",
        weight_loss_reasons: "health",
        target_weight_kg: null,
        target_weight_lbs: null,
        target_weight_in: "kg",
        activity_level: "moderately_active",
        diet_type: "classic",
        intermittent_fasting: "No",
        fasting_window_type: "16:8",
      },
    };
  }

  // Update user goals
  async updateUserGoals(goalsData: GoalsData): Promise<GoalsApiResponse> {
    const response = await put<GoalsApiResponse>("/food_scanner/goal/", {
      goals: goalsData,
    });

    return response;
  }

  // Format weight loss goal for display
  formatWeightLossGoal(goal: string): string {
    const mapping: { [key: string]: string } = {
      yes: "Yes",
      no: "No",
      not_sure: "Not Sure",
    };
    return mapping[goal] || "Not Sure";
  }

  // Format activity level for display
  formatActivityLevel(level: string): string {
    const mapping: { [key: string]: string } = {
      sedentary: "Sedentary",
      lightly_active: "Lightly Active",
      moderately_active: "Moderately Active",
      very_active: "Very Active",
      extremely_active: "Extremely Active",
    };
    return mapping[level] || "Moderately Active";
  }

  // Format diet type for display
  formatDietType(diet: string): string {
    const mapping: { [key: string]: string } = {
      classic: "Classic",
      keto: "Keto",
      paleo: "Paleo",
      mediterranean: "Mediterranean",
      vegetarian: "Vegetarian",
      vegan: "Vegan",
      other: "Other",
    };
    return mapping[diet] || "Classic";
  }

  // Format intermittent fasting for display
  formatIntermittentFasting(fasting: string): string {
    return fasting || "No";
  }

  // Format fasting window type for display
  formatFastingWindowType(window: string): string {
    return window || "16:8";
  }

  // Get target weight based on preferred unit
  getTargetWeight(
    goals: GoalsApiResponse["goals"],
    preferredUnit: string
  ): number {
    if (preferredUnit === "lbs") {
      return (
        goals.target_weight_lbs ??
        (goals.target_weight_kg ? goals.target_weight_kg * 2.205 : 0)
      );
    } else {
      return (
        goals.target_weight_kg ??
        (goals.target_weight_lbs ? goals.target_weight_lbs / 2.205 : 0)
      );
    }
  }
}

export default GoalsService.getInstance();
